// ---------------------------------------------------------------------------
// brendaDates — date validation + duration helpers
// ---------------------------------------------------------------------------
//
// Self-contained date helpers for the `useBrendaCharges` flow. Fresh
// copies of the functionality previously imported from
// `app/utils/hirePricing.ts`, `app/utils/date.ts` and
// `app/utils/basket.ts`, so the new charges flow owns its own rules and
// can evolve without touching legacy code paths.
//
// Two things live here:
//
//   1. `validateBrendaHireDates(req, holidays)` — checks submitted
//      start/end dates against bank holidays. Start dates always
//      block on a holiday; end dates block when `automate` is unset,
//      otherwise bump forward past the holiday.
//
//   2. Duration / end-date helpers used by `evaluateBrendaCharges`
//      to compute a corrected end date that preserves the user's
//      requested hire duration.
// ---------------------------------------------------------------------------

// Types
import type { HssBankHolidayItem } from '~/types'

// ---------------------------------------------------------------------------
// Local date primitives
// ---------------------------------------------------------------------------

/**
 * Formats a Date (or date-compatible string) as `YYYY-MM-DD`. Used for
 * ISO-safe comparisons and for the values written back to the API.
 */
const toBrendaIsoDate = (date: Date): string => {
  const yyyy = String(date.getUTCFullYear()).padStart(4, '0')
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(date.getUTCDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Formats a date for UI display: `DD.MM.YYYY`. Used in the bank-holiday
 * error messages below.
 */
const toBrendaDisplayDate = (isoDate: string): string => {
  const [y, m, d] = isoDate.split('-')
  return `${d}.${m}.${y}`
}

/**
 * Parses a `YYYY-MM-DD` string into a UTC Date to avoid DST edge cases
 * when adding days.
 */
const parseBrendaIsoDate = (isoDate: string): Date => {
  const [y, m, d] = isoDate.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

/**
 * Adds `days` to an ISO date string, returning the new `YYYY-MM-DD`.
 */
const addDaysToBrendaIsoDate = (isoDate: string, days: number): string => {
  const date = parseBrendaIsoDate(isoDate)
  date.setUTCDate(date.getUTCDate() + days)
  return toBrendaIsoDate(date)
}

/**
 * Number of whole days between two ISO dates (inclusive-ish, always
 * rounds up, minimum 1). Matches the rental duration calculation used
 * elsewhere in the app.
 */
export const checkBrendaDaysBetween = (
  startDate: string,
  endDate: string
): number => {
  const start = parseBrendaIsoDate(startDate).getTime()
  const end = parseBrendaIsoDate(endDate).getTime()
  const diff = Math.abs(end - start)
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24))
  return days < 1 ? 1 : days
}

/**
 * True when `isoDate` is a Saturday or Sunday in UTC.
 */
const isBrendaWeekend = (isoDate: string): boolean => {
  const day = parseBrendaIsoDate(isoDate).getUTCDay()
  return day === 0 || day === 6
}

/**
 * True when `isoDate` matches one of the cached bank holiday entries.
 */
const isBrendaBankHoliday = (
  holidays: HssBankHolidayItem[] | null,
  isoDate: string
): boolean => (holidays ?? []).some(h => h.date === isoDate)

/**
 * Finds the bank holiday metadata for a given date, if any.
 */
const findBrendaHoliday = (
  holidays: HssBankHolidayItem[] | null,
  isoDate: string
): HssBankHolidayItem | undefined =>
  (holidays ?? []).find(h => h.date === isoDate)

// ---------------------------------------------------------------------------
// End-date correction
// ---------------------------------------------------------------------------

/**
 * Ensures an end date doesn't land on a weekend or bank holiday. If it
 * does, bumps forward one day at a time until a valid date is found or
 * we've exhausted `maxBumps` attempts. Returns `null` when no valid
 * date can be found inside the bump window.
 */
const getBrendaValidEndDate = (
  endDate: string,
  holidays: HssBankHolidayItem[] | null,
  maxBumps = 12
): string | null => {
  const isInvalid = (iso: string) =>
    isBrendaWeekend(iso) || isBrendaBankHoliday(holidays, iso)

  if (!isInvalid(endDate)) return endDate

  let candidate = endDate
  for (let i = 0; i < maxBumps; i++) {
    candidate = addDaysToBrendaIsoDate(candidate, 1)
    if (!isInvalid(candidate)) return candidate
  }

  return null
}

/**
 * Derives a hire end date from a start date + duration, bumping past
 * any weekend or bank holiday days that land on the computed end. The
 * result is always a valid hire end date (weekday, non-holiday).
 */
export const chooseBrendaHireEndDate = (
  daysRequested: number,
  startDate: string,
  holidays: HssBankHolidayItem[] | null
): string => {
  const rawEnd = addDaysToBrendaIsoDate(startDate, daysRequested)
  return getBrendaValidEndDate(rawEnd, holidays) ?? rawEnd
}

// ---------------------------------------------------------------------------
// Bank-holiday validation
// ---------------------------------------------------------------------------

interface BrendaDateValidationRequest {
  startDate: string
  endDate: string
  /**
   * When true, the validator bumps an end date forward past a holiday
   * rather than failing — used by flows that re-run a previously-valid
   * search after a date shift.
   */
  automate?: boolean
}

type BrendaDateValidationResult =
  | { ok: true; adjustedEndDate: string }
  | { ok: false; message: string }

const CLOSED_RANGE_COPY = '25th Dec to 2nd Jan, and until 5th Jan in Scotland'

/**
 * Validates the start and end dates on a charges submit against the
 * cached bank holidays.
 *
 *   - Start on a bank holiday → always blocks (no auto-adjust because
 *     the start date is user-visible and shouldn't move silently).
 *   - End on a bank holiday + `automate` unset → blocks with an error.
 *   - End on a bank holiday + `automate` set → bumps forward via
 *     `getBrendaValidEndDate`.
 */
export const validateBrendaHireDates = (
  req: BrendaDateValidationRequest,
  holidays: HssBankHolidayItem[] | null
): BrendaDateValidationResult => {
  // 1. Start on a bank holiday — always block.
  if (isBrendaBankHoliday(holidays, req.startDate)) {
    const holiday = findBrendaHoliday(holidays, req.startDate)
    const isPause = holiday?.name?.toLowerCase().includes('pause')
    const displayStartDate = toBrendaDisplayDate(req.startDate)

    return {
      ok: false,
      message: isPause
        ? `We cannot deliver on ${displayStartDate} as we are closed between ${CLOSED_RANGE_COPY}.`
        : `Date selection cannot start on ${displayStartDate} as this is a bank holiday.`,
    }
  }

  // 2. End date already valid — pass through unchanged.
  if (!isBrendaBankHoliday(holidays, req.endDate)) {
    return { ok: true, adjustedEndDate: req.endDate }
  }

  // 3. End date falls on a holiday.
  const endHoliday = findBrendaHoliday(holidays, req.endDate)
  const endIsPause = endHoliday?.name?.toLowerCase().includes('pause')
  const displayEndDate = toBrendaDisplayDate(req.endDate)

  if (!req.automate) {
    return {
      ok: false,
      message: endIsPause
        ? `We cannot collect on ${displayEndDate} as we are closed between ${CLOSED_RANGE_COPY}.`
        : `Date selection cannot end on ${displayEndDate} as this is a bank holiday.`,
    }
  }

  // 4. Automating — bump end date forward past holidays.
  const adjustedEndDate = getBrendaValidEndDate(req.endDate, holidays)

  if (!adjustedEndDate) {
    return {
      ok: false,
      message:
        "Sorry, it looks like we couldn't find valid dates for your selection.",
    }
  }

  return { ok: true, adjustedEndDate }
}

